import type { ReactElement } from "react";

import { useEffect, useState } from "react";

import { Breadcrumb, IconButton, SearchField } from "../design-system";

/**
 * Die **Kopfleiste**. Sie beginnt rechts neben der Sidebar, ist `--h-topbar`
 * hoch, liegt auf `--bg-chrome` und trägt **keine untere Trennlinie** — die Kante
 * entsteht durch die Inhaltsfläche darunter (`FRAME.md`, „Kopfleiste").
 *
 * Von links: der Sidebar-Schalter, ein 22px-Trenner in `--line-d`, die Brotkrume;
 * dann `margin-left: auto` und rechts das, was es hier gibt.
 *
 * **Die Brotkrume ist der Titel des Screens** — einstufig auf Übersichten,
 * zweistufig in Detailansichten. Hier steht die Mechanik: die Brotkrume,
 * Favoritenstern und Aktionsmenü daneben, und der Dokumenttitel, der ihr folgt.
 * **Welche** Einträge darin stehen, füllt `App.tsx`: seit #41 zweistufig für die
 * geöffnete Revision, mit dem Rückweg auf dem ersten Glied; die übrigen
 * Detailansichten zieht #136 nach.
 *
 * **Der Baustein kommt seit #41 aus `components/navigation/`** und damit aus dem
 * Baukasten des Skills. Sein Vertrag heißt `trail` statt `items` und führt kein
 * `trailing` mehr: Stern und Menü folgen dem letzten Glied **neben** der Brotkrume, wie im
 * Referenzgerüst. Die Umstellung war in #40 und #41 doppelt vorgesehen und
 * zwischen beiden hindurchgefallen.
 *
 * **Rechts fehlt, was es hier nicht gibt.** Glocke, Nachrichten und der
 * Dashboard-Sprung der Vorlage fallen: Es gibt keine Melder und kein
 * Dashboard. Ausgeschrieben steht das in `design/VENDOR.md`.
 *
 * **Die globale Suche steht seit #157 da, abgeschaltet.** Es gibt keinen Index
 * über Revisionen, Befunde und Bestand — das Feld sagt das über sein `title`,
 * statt zu verschwinden: Der Rahmen sieht die Suche an dieser Stelle vor, und
 * ein leeres Feld, das nichts findet, ist ehrlicher als eine Lücke, die aussieht
 * wie ein vergessener Baustein.
 *
 * **Die drei vorläufigen Bewohner sind mit #135 ausgezogen**: die laufende Fassung
 * steht in der Logo-Zeile der Sidebar und öffnet dort das Fassungsmenü, „Angemeldet
 * als" samt Rolle und der Abmeldeknopf stehen im Benutzerfuß. Sie standen hier, weil
 * das Produkt zwischen #40 und #135 bedienbar bleiben musste; der Rahmen hat für sie
 * rechts in der Leiste keinen Platz. **Der Verbindungszustand ist mit #157
 * gefallen** — die Leiste steht nur über einer Sitzung, also war er dort immer
 * derselbe und keine Auskunft.
 */
export function TopBar({
  trail,
  onNavigate,
  onToggleSidebar,
}: {
  /** Die Brotkrume, von der Wurzel zum Ort. Der letzte Eintrag ist der Titel. */
  trail: string[];
  /**
   * Ein Klick auf ein Glied **vor** dem letzten — der Weg zurück in der
   * Brotkrume. Das letzte Glied ist der Ort selbst und nicht anklickbar.
   *
   * Wohin er führt, weiß die Anwendung und nicht die Leiste: `App.tsx` schließt
   * damit die geöffnete Revision und steht wieder in der Liste, aus der sie
   * geöffnet wurde.
   */
  onNavigate?: (label: string, index: number) => void;
  onToggleSidebar: () => void;
}): ReactElement {
  useDocumentTitle(trail[trail.length - 1] ?? "");

  return (
    <header
      style={{
        position: "relative",
        // Über der Inhaltsfläche: Was aus dieser Leiste aufklappt, klappt über
        // den Inhalt und nicht darunter.
        zIndex: 40,
        height: "var(--h-topbar)",
        flex: "none",
        display: "flex",
        alignItems: "center",
        // Die Maße der Vorlage, unverändert: `gap: 22`, `padding: 0 20px 0 23px`
        // (`FRAME.md`, „Kopfleiste"; `reference/AppShell.jsx`). Für 22 und 23
        // führt das System kein Token, und der Rahmen schreibt sie selbst als
        // Zahl — das Polster hier aufzuteilen, halb Token und halb Zahl, machte
        // aus einem abgelesenen Maßpaar zwei Werte mit verschiedener Herkunft.
        gap: 22,
        paddingLeft: 23,
        paddingRight: 20,
        background: "var(--bg-chrome)",
      }}
    >
      {/* Der Ausgleich von -5 steht in der Vorlage: Er rückt die Fläche des
          32px-Knopfes so, dass die Glyphe auf der Fluchtlinie sitzt. `size="lg"`
          ist seit #157 die richtige Stufe dafür — die Kopfleiste ist laut
          `FRAME.md` die einzige Stelle mit der 20px-Glyphe. */}
      <IconButton
        icon="panel-left"
        tip="Navigation ein-/ausblenden"
        size="lg"
        tone="quiet"
        onClick={onToggleSidebar}
        style={{ marginLeft: -5, marginRight: -5 }}
      />

      <div style={{ width: 1, height: 22, background: "var(--line-d)" }} />

      {/* **Die Handlungen stehen neben der Brotkrume, nicht in ihr.** Der
          Baustein des Skills führt kein `trailing`, und das Referenzgerüst
          beantwortet die Stelle genauso (`design/reference/AppShell.jsx`): Stern
          und Menü folgen dem letzten Glied als Geschwister. */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-4)", minWidth: 0, flex: 1, overflow: "hidden" }}>
        <Breadcrumb trail={trail} onNavigate={onNavigate} />
        <DetailActions detail={trail.length > 1} />
      </div>

      <div
        style={{
          marginLeft: "auto",
          flex: "none",
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-10)",
        }}
      >
        {/* Die globale Suche steht laut `FRAME.md`, „Kopfleiste", auf 280×
            `--h-search` mit `⌘F` rechts im Feld — abgeschaltet, denn es gibt
            hier keinen Index über Revisionen, Befunde und Bestand. Ein
            `<fieldset disabled>` schaltet Feld und Eingabe ohne Eingriff in
            den Baustein der Kopie ab; das `title` am Rahmen sagt, warum das
            Feld nichts findet. */}
        <fieldset
          disabled
          title="Es gibt hier keinen Index über Revisionen, Befunde und Bestand."
          style={{ position: "relative", border: "none", margin: 0, padding: 0, cursor: "not-allowed" }}
        >
          <SearchField placeholder="Suchen …" showEsc={false} style={{ width: 280, height: "var(--h-search)" }} />
          <span
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "var(--fs-10)",
              color: "var(--fg-9)",
              pointerEvents: "none",
            }}
          >
            ⌘F
          </span>
        </fieldset>
      </div>
    </header>
  );
}

/**
 * Der Dokumenttitel folgt der Brotkrume: `<letzter Eintrag> · SilverERP-Devtools`
 * (`FRAME.md`, „Dokumenttitel").
 *
 * Er hängt am letzten Eintrag und nicht an der Liste: Die Liste ist bei jedem
 * Zeichnen eine neue, der Eintrag ist eine Zeichenkette.
 */
function useDocumentTitle(last: string): void {
  useEffect(() => {
    if (last !== "") {
      document.title = `${last} · SilverERP-Devtools`;
    }
  }, [last]);
}

/**
 * Favoritenstern und Aktionsmenü, die dem letzten Eintrag folgen — **nur in einer
 * Detailansicht**, wie in der Vorlage (`trail.length > 1`).
 *
 * Seit #41 gibt es den zweistufigen Weg: Der Revisionsstrom öffnet eine Revision,
 * und beide erscheinen. Der Stern merkt sich seine Wahl für diesen Aufenthalt; ein
 * Bestand an Favoriten ist nicht Teil dieses Produkts, und der Eintrag
 * „Favoriten" fällt in #135 ausdrücklich weg. Hinter `more-vertical` steht noch
 * nichts: Die Knöpfe der geöffneten Revision — Rückweg, Übergabe, Schrittschalter
 * — stehen in ihrer **Werkzeugleiste**, und ein Menü daneben wäre ein zweiter Ort
 * für dieselben Handlungen.
 */
function DetailActions({ detail }: { detail: boolean }): ReactElement | null {
  const [favorite, setFavorite] = useState(false);

  if (!detail) {
    return null;
  }

  return (
    <>
      <IconButton
        icon="star"
        tip="Als Favorit merken"
        size="sm"
        // Der Unterschied zwischen gemerkt und nicht gemerkt steht im Ton und
        // nicht mehr in der Füllung — der Baustein des Skills führt kein `fill`,
        // und das Referenzgerüst beantwortet genau diese Stelle so
        // (`design/reference/AppShell.jsx`): `--ac-amber-3` gemerkt, `--fg-7` nicht.
        tone={favorite ? "favorite" : "quiet"}
        onClick={() => setFavorite(!favorite)}
      />
      <IconButton icon="more-vertical" tip="Weitere Aktionen" size="sm" tone="quiet" />
    </>
  );
}
